import pickBy from "lodash/pickBy";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import assign from "lodash/assign";
import { getHighestComponentOrderAndGroupOrderForGroup } from "./orderingComponents";
import { objIsNotEmpty, handleError, notEmpty } from "../../utils/basic";
import {
  sortingComponentLabels,
  sortingGroupLabels,
} from "./orderingComponents";
import { sortByVersionConvertedToString } from "../../utils/helpers";
import { getCachedUrl, getFreshUrl } from "../../utils/StorageManager";
import {
  getDocumentConversionJob,
  startDocumentConversion,
} from "../../data/documentConversionClient";
import {
  pollVideoPosterJob,
  signReadyVideoPoster,
  startVideoPoster,
  VIDEO_POSTER_STATUS,
} from "../../data/videoPosterClient";
import { COMPONENT_UPLOADER_ROLE } from "../../content/componentsRules";
import { groups as FileFormatGroups } from "../../content/workFileFormatsGroups";
import { isDawFolderComponent } from "../../utils/folderUploadDaw";
import {
  getFileExtFromCleanedExt,
  getGroupFromFileType,
} from "../../utils/fileUploadHelpers";
import { v4 as uuid } from "uuid";

export const FOLDER_UPLOAD_COMPONENT_GROUP = "Folders";
const DOCUMENT_PREVIEW_FILETYPES = ["doc", "docx", "ppt", "pptx", "rtf"];
const DOCUMENT_PREVIEW_POLL_INTERVAL_MS = 3000;
const DOCUMENT_PREVIEW_MAX_POLL_ATTEMPTS = 20;
const documentPreviewGenerationPromises = new Map();

export function getMediaLinkPurpose({
  componentType,
  filetype,
  mimetype,
}) {
  const normalizedFiletype = String(filetype || "");
  const normalizedMimetype = String(mimetype || "");
  const mediaGroup = getGroupFromFileType(
    getFileExtFromCleanedExt(normalizedFiletype),
    normalizedMimetype
  );
  const isVideo =
    componentType === "Video" ||
    mediaGroup === FileFormatGroups.VIDEO_FILE;
  if (isVideo) return "original";

  const isImage =
    componentType === "Image" ||
    mediaGroup === FileFormatGroups.IMAGE_FILE;
  if (isImage) return "original";

  const isVideoMimetype = normalizedMimetype
    .toLowerCase()
    .startsWith("video/");
  const canBrowserPlayAudio = Boolean(
    normalizedMimetype &&
    !isVideoMimetype &&
    new Audio().canPlayType(normalizedMimetype)
  );
  const isAudio =
    !isVideoMimetype &&
    (componentType === "Audio" ||
      mediaGroup === FileFormatGroups.AUDIO_FILE) &&
    (!normalizedMimetype || canBrowserPlayAudio);

  return isAudio ? "audio" : "combined";
}

export const getProgress = (progress) => {
  return Math.ceil((100 * progress.loaded) / progress.total);
};

export const setProgressBar = (progress, setUploadProgress) => {
  const uploadProgress = Math.ceil((100 * progress.loaded) / progress.total);
  setUploadProgress(uploadProgress);
};

export const isObjectWithoutFalsyValues = (payloads) => {
  for (const key in payloads) {
    const objWithFalsyValue = pickBy(payloads[key], (value) => value == null);
    if (!isEmpty(objWithFalsyValue)) {
      return false;
    }
  }
  return true;
};

export const onGetComponentGroupAndOrder = ({ group, componentsInGroup }) => {
  return getHighestComponentOrderAndGroupOrderForGroup(
    getOrderedComponents(componentsInGroup),
    group
  );
};

export const getOrderedComponents = (componentsWithLabels) => {
  if (isEmpty(componentsWithLabels)) return [];
  const orderedComponentsGroup = [];
  componentsWithLabels.forEach((componentsWithLabel) => {
    let updatedComponentWithLabel = {
      componentGroup: componentsWithLabel.componentGroup,
      componentOrderWeight: componentsWithLabel.componentOrderWeight,
      componentGroupOrderWeight: componentsWithLabel.componentGroupOrderWeight,
    };
    assign(
      updatedComponentWithLabel,
      get(componentsWithLabel, "component[0]", componentsWithLabel)
    );
    if (updatedComponentWithLabel.isFolderUpload === true) {
      updatedComponentWithLabel.componentGroup = isDawFolderComponent(
        updatedComponentWithLabel
      )
        ? FileFormatGroups.DAW_PROJECT_FILE
        : FOLDER_UPLOAD_COMPONENT_GROUP;
    }
    if (!isEmpty(updatedComponentWithLabel.versions)) {
      updatedComponentWithLabel.versions = updatedComponentWithLabel.versions
        .slice()
        .sort(sortByVersionConvertedToString);
    }
    const foundGroup = orderedComponentsGroup.find(
      (componentGroup) =>
        componentGroup.group === updatedComponentWithLabel.componentGroup
    );
    if (objIsNotEmpty(foundGroup)) {
      foundGroup.components.push(updatedComponentWithLabel);
    } else {
      const components = [updatedComponentWithLabel];
      orderedComponentsGroup.push({
        group: updatedComponentWithLabel.componentGroup,
        components,
      });
    }
  });
  orderedComponentsGroup.forEach((group) => {
    group.components.sort(sortingComponentLabels);
  });
  orderedComponentsGroup.sort(sortingGroupLabels);
  return orderedComponentsGroup;
};

export const buildPayloadAndMakeRequest = (
  state,
  accountId,
  projectId,
  personId,
  setPayloads,
  setCurrentFileUploaded,
  setUploadProgress
) => {
  const inputs = [];
  let {
    componentGroupOrderWeight,
    componentOrderWeight,
  } = onGetComponentGroupAndOrder(state.componentGroup);

  state.files.forEach((fileWrap) => {
    const metadata = {
      contentLength: fileWrap.file.size.toString(),
      lastModfified: fileWrap.file.lastModified.toString(),
    };
    const storageInput = {
      file: fileWrap.file,
      metadata,
      contentType: fileWrap.file.type,
      progressCallback: (progress) =>
        setProgressBar(progress, setUploadProgress),
    };
    const componentInput = {
      title: fileWrap.title && fileWrap.title.trim(),
      ...(fileWrap.isSourceFile && {
        software: fileWrap.software && fileWrap.software.trim(),
      }),
      version: "1",
    };
    const fileInput = {
      fileName: fileWrap.file.name,
      fileType: fileWrap.fileExtensionEnum,
      mimeType: fileWrap.file.type,
      size: fileWrap.file.size,
    };

    const payload = {
      accountId: accountId,
      partyId: personId,
      projectId,
      componentInput,
      fileInput,
      componentGroup: fileWrap.componentGroup.trim(),
      componentGroupOrderWeight,
      componentOrderWeight,
    };

    const input = {
      payload,
      sourceFile: fileWrap.isSourceFile,
      storageInput,
      fileInput,
    };

    if (isObjectWithoutFalsyValues(input)) {
      inputs.push(input);
    } else {
      handleError("Invalid input");
    }
  });

  setPayloads(inputs);
  setCurrentFileUploaded(inputs[0]);
};

export const buildComponentPayload = (
  state,
  projectId,
  personId,
  setPayloads,
  setCurrentFileUploaded,
  setUploadProgress
) => {
  if (state && state.files && state.files.length) {
    const inputs = [];
    let {
      componentGroupOrderWeight,
      componentOrderWeight,
    } = onGetComponentGroupAndOrder(state.componentGroup);

    state.files.forEach((fileWrap) => {
      const metadata = {
        contentLength: fileWrap.file.size.toString(),
        lastModfified: fileWrap.file.lastModified.toString(),
      };
      const storageInput = {
        file: fileWrap.file,
        metadata,
        contentType: fileWrap.file.type,
        progressCallback: (progress) =>
          setProgressBar(progress, setUploadProgress),
      };
      const componentInput = {
        title: fileWrap.title && fileWrap.title.trim(),
        ...(fileWrap.isSourceFile && {
          software: fileWrap.software && fileWrap.software.trim(),
        }),
        version: "1",
      };
      const fileInput = {
        fileName: fileWrap.file.name,
        fileType: fileWrap.fileExtensionEnum,
        mimeType: fileWrap.file.type,
        size: fileWrap.file.size,
      };

      const payload = {
        partyId: personId,
        projectId,
        componentInput,
        fileInput,
        componentGroup: fileWrap.componentGroup.trim(),
        componentGroupOrderWeight,
        componentOrderWeight,
      };

      const input = {
        payload,
        sourceFile: fileWrap.isSourceFile,
        storageInput,
        fileInput,
      };

      if (isObjectWithoutFalsyValues(input)) {
        inputs.push(input);
      } else {
        console.warn("Invalid input");
      }
    });

    setPayloads(inputs);
    setCurrentFileUploaded(inputs[0]);

    return true;
  }
};

export function getFileInput({
  uri,
  fileName,
  fileType,
  mimeType,
  size,
  bitDepth,
  samplingRate,
}) {
  return {
    uri,
    fileName,
    fileType,
    mimeType,
    size,
    bitDepth,
    samplingRate,
  };
}

export function getComponentInput(title, version, software) {
  return {
    id: uuid(),
    title,
    ...(software && { software }),
    version: `${version}`,
  };
}

export function getLastNumber(string) {
  if (!string) {
    return "";
  }
  return +new String(string).match(/\d+$/)[0];
}

export function computeComponentVersion(originalVersion) {
  const lastNumber = getLastNumber(originalVersion);
  return {
    getNewNested: `${originalVersion}-1`,
    increase: new String(originalVersion).replace(/\d+$/, +lastNumber + 1),
  };
}

export async function getStreamingLink(identityId, key) {
  try {
    const url = await getCachedUrl({ identityId, key });
    return url;
  } catch (e) {
    handleError("Error generation streaming link" + e);
  }
}

function getFileExtensionFromKey(key) {
  const fileName = String(key || "").split("/").pop() || "";
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) return "";
  return fileName.slice(dotIndex + 1).toLowerCase();
}

function canLazyGenerateDocumentPreview(key) {
  return DOCUMENT_PREVIEW_FILETYPES.includes(getFileExtensionFromKey(key));
}

function buildProtectedSourceKey(identityId, key) {
  if (!identityId || !key) return "";
  if (key.startsWith("protected/")) return key;
  return `protected/${identityId}/${key}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function isDocumentPreviewPdfAvailable(documentPreviewPdfUrl) {
  if (!documentPreviewPdfUrl || typeof fetch !== "function") return false;
  const response = await fetch(documentPreviewPdfUrl);
  return response.ok;
}

async function pollDocumentConversionJob({
  jobId,
  personId,
  accountId,
  pollIntervalMs = DOCUMENT_PREVIEW_POLL_INTERVAL_MS,
  maxPollAttempts = DOCUMENT_PREVIEW_MAX_POLL_ATTEMPTS,
}) {
  for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
    const job = await getDocumentConversionJob({ jobId, personId, accountId });
    if (!job) return null;
    if (job.status === "READY" || job.status === "FAILED") return job;
    if (attempt < maxPollAttempts - 1) {
      await sleep(pollIntervalMs);
    }
  }
  return null;
}

async function ensureDocumentPreviewPdf({
  identityId,
  key,
  documentPreviewPdfUrl,
  documentPreviewGeneration,
}) {
  if (
    !documentPreviewGeneration ||
    !canLazyGenerateDocumentPreview(key) ||
    !documentPreviewPdfUrl
  ) {
    return;
  }

  const previewExists = await isDocumentPreviewPdfAvailable(documentPreviewPdfUrl);
  if (previewExists) return;

  const personId = documentPreviewGeneration.personId;
  const accountId = documentPreviewGeneration.accountId;
  const componentId = documentPreviewGeneration.componentId;
  const sourceKey = buildProtectedSourceKey(identityId, key);

  if (!personId || !accountId || !componentId || !sourceKey) return;

  if (documentPreviewGenerationPromises.has(sourceKey)) {
    return documentPreviewGenerationPromises.get(sourceKey);
  }

  const generationPromise = startAndPollDocumentPreviewGeneration({
    personId,
    accountId,
    componentId,
    sourceKey,
    documentPreviewGeneration,
  });
  documentPreviewGenerationPromises.set(sourceKey, generationPromise);

  try {
    await generationPromise;
  } finally {
    documentPreviewGenerationPromises.delete(sourceKey);
  }
}

async function startAndPollDocumentPreviewGeneration({
  personId,
  accountId,
  componentId,
  sourceKey,
  documentPreviewGeneration,
}) {
  const job = await startDocumentConversion({
    personId,
    accountId,
    componentId,
    sourceKey,
  });

  if (!job || job.status === "FAILED") {
    throw new Error("Document preview generation failed");
  }

  if (job.status === "READY") return;

  const completedJob = await pollDocumentConversionJob({
    jobId: job.jobId,
    personId,
    accountId,
    pollIntervalMs: documentPreviewGeneration.pollIntervalMs,
    maxPollAttempts: documentPreviewGeneration.maxPollAttempts,
  });

  if (!completedJob || completedJob.status !== "READY") {
    throw new Error("Document preview generation did not complete");
  }
}

export function getlatestVersionsByProject(allComponents, personId, projectId) {
  function getLatestVersionUploadedByUser(component) {
    if (component.versions && component.versions.length) {
      const latestVersion = component.versions.find((c) => {
        // If a file is from a different project exclude it
        const componentProjectId = get(component, "project[0].id", "");
        const versionProjectId = get(c, "project[0].id", "");
        if (componentProjectId !== versionProjectId) return false;

        // must be uploaded by this user
        const uploaderContribution = (c.isContributionOf || []).find(
          (contr) => contr.role === COMPONENT_UPLOADER_ROLE
        );
        if (uploaderContribution) {
          const uploaderId = get(uploaderContribution, "contributor[0].id");
          return uploaderId && uploaderId === personId;
        }
        return false;
      });
      return latestVersion ? latestVersion : component;
    }
    return component;
  }

  const unassigned = [];

  let byProject = {};

  if (personId && allComponents) {
    byProject = allComponents.reduce((separatedByProject, c) => {
      const latest = getLatestVersionUploadedByUser(c);
      const latestProject = get(
        latest,
        "project[0]",
        get(latest, "referrer[0]")
      );
      const isNotInCurrentProject =
        latestProject && latestProject.id !== projectId;
      if (latest && isNotInCurrentProject) {
        if (latestProject && latestProject.isArchived)
          return separatedByProject;
        const projectName = latestProject && latestProject.title;
        if (projectName) {
          if (
            Object.prototype.hasOwnProperty.call(
              separatedByProject,
              projectName
            ) &&
            separatedByProject[projectName].length &&
            !separatedByProject[projectName].some((c) => c.id === latest.id)
          ) {
            separatedByProject[projectName].push(latest);
          } else {
            separatedByProject[projectName] = [latest];
          }
        } else {
          handleError(
            "There is a component without a project title in the import list, skipping it"
          );
        }
      } else if (!latestProject) {
        unassigned.push(latest);
      }

      return separatedByProject;
    }, {});
  }

  return {
    unassigned,
    byProject,
    length: unassigned.length + Object.values(byProject).length,
  };
}

export function getContributorsFromContributions(contributions) {
  return contributions.reduce((acc, current) => {
    const contributor = get(current, "contributor[0]", null);
    if (contributor) {
      acc.push(contributor);
    }
    return acc;
  }, []);
}

export async function getOriginalMediaLink({ identityId, key }) {
  if (!identityId || !key) return {};
  const url = await getStreamingLink(identityId, key);
  return { url };
}

export async function getAudioMediaLinks({ identityId, key }) {
  if (!identityId || !key) return {};
  const url = await getStreamingLink(identityId, key);
  const peaksLink = await getStreamingLink(identityId, `${key}.json`);
  return { url, peaksLink };
}

export async function getLegacyThumbnailLink({ identityId, key }) {
  if (!identityId || !key) return {};
  const thumbnail = await getStreamingLink(identityId, `${key}.jpeg`);
  return { thumbnail };
}

export async function getWorkThumbnailLink({
  identityId,
  key,
  componentType,
  filetype,
  mimetype,
  personId,
  accountId,
  projectId,
  componentId,
}) {
  if (!identityId || !key) return {};

  const normalizedType = String(componentType || "").toLowerCase();
  const normalizedMime = String(mimetype || "").toLowerCase();
  const normalizedFiletype = String(filetype || "")
    .replace(/^\./, "")
    .toLowerCase();
  const isVideo =
    normalizedType === "video" || normalizedMime.startsWith("video/");
  const isImage =
    normalizedType === "image" || normalizedMime.startsWith("image/");

  if (isVideo) {
    const sourceKey = key.startsWith("protected/")
      ? key
      : `protected/${identityId}/${key}`;
    const videoUrl = await getStreamingLink(identityId, key);

    try {
      const input = {
        personId,
        accountId,
        projectId,
        videoId: componentId,
        sourceKey,
      };
      const initialJob = await startVideoPoster(input);
      if (initialJob.status === VIDEO_POSTER_STATUS.READY) {
        const poster = await signReadyVideoPoster(
          initialJob.sourceKey || sourceKey
        );
        if (poster) return { thumbnail: poster, thumbnailType: "image" };
      } else if (
        initialJob.status === VIDEO_POSTER_STATUS.PENDING ||
        initialJob.status === VIDEO_POSTER_STATUS.CONVERTING
      ) {
        pollVideoPosterJob(input)
          .then(job => {
            if (job && job.status === VIDEO_POSTER_STATUS.READY) {
              return signReadyVideoPoster(job.sourceKey || sourceKey);
            }
            return "";
          })
          .catch(() => {});
      }
    } catch (error) {
      // The signed source below remains a usable first-frame fallback.
    }

    return videoUrl
      ? { thumbnail: videoUrl, thumbnailType: "video" }
      : {};
  }

  if (isImage) {
    const thumbnail = await getStreamingLink(identityId, key);
    return thumbnail ? { thumbnail, thumbnailType: "image" } : {};
  }

  if (
    normalizedFiletype === "pdf" ||
    normalizedMime === "application/pdf"
  ) {
    const thumbnail = await getStreamingLink(identityId, key);
    return thumbnail ? { thumbnail, thumbnailType: "document" } : {};
  }

  if (canLazyGenerateDocumentPreview(key)) {
    const result = await getDocumentPreviewLink({
      identityId,
      key,
      documentPreviewGeneration: {
        personId,
        accountId,
        componentId,
      },
    });
    const thumbnail = result && result.documentPreviewPdfUrl;
    return thumbnail ? { thumbnail, thumbnailType: "document" } : {};
  }

  return {};
}

export async function getDocumentPreviewLink({
  identityId,
  key,
  documentPreviewGeneration,
}) {
  try {
    if (!identityId || !key) return {};
    const previewKey = `${key}.pdf`;
    const documentPreviewPdfUrl = await getFreshUrl({
      identityId,
      key: previewKey,
    });

    await ensureDocumentPreviewPdf({
      identityId,
      key,
      documentPreviewPdfUrl,
      documentPreviewGeneration,
    });

    return {
      documentPreviewPdfUrl: await getFreshUrl({
        identityId,
        key: previewKey,
      }),
    };
  } catch (e) {
    handleError(e);
    handleError("Error generation download link");
  }
}

export async function getStreamAndPeakLinks({
  identityId,
  key,
  documentPreviewGeneration,
}) {
  try {
    if (!identityId || !key) return {};
    const url = await getStreamingLink(identityId, key);

    const peaksLink = await getStreamingLink(identityId, key + ".json");

    const thumbnail = await getStreamingLink(identityId, key + ".jpeg");

    const previewKey = `${key}.pdf`;
    const shouldRefreshDocumentPreview =
      documentPreviewGeneration && canLazyGenerateDocumentPreview(key);
    const documentPreviewPdfUrl = shouldRefreshDocumentPreview
      ? await getFreshUrl({ identityId, key: previewKey })
      : await getStreamingLink(identityId, previewKey);

    await ensureDocumentPreviewPdf({
      identityId,
      key,
      documentPreviewPdfUrl,
      documentPreviewGeneration,
    });

    const refreshedDocumentPreviewPdfUrl = shouldRefreshDocumentPreview
      ? await getFreshUrl({ identityId, key: previewKey })
      : documentPreviewPdfUrl;

    return {
      peaksLink,
      url,
      thumbnail,
      documentPreviewPdfUrl: refreshedDocumentPreviewPdfUrl,
    };
  } catch (e) {
    handleError(e);
    handleError("Error generation download link");
  }
}

export function getUploader(component) {
  if (!component) return "";
  if (!notEmpty(component.isContributionOf)) return "";
  const uploaderContribution = component.isContributionOf.find(
    (contribution) => contribution.role === "uploader"
  );
  return get(uploaderContribution, "contributor[0]", null);
}
